/**
 * Parwaaz — shared domain types.
 * This file is the contract between the web app, the backend core and every adapter.
 * Keep it dependency-free. Dates are ISO strings (YYYY-MM-DD); timestamps are ISO 8601 UTC.
 */

export type ISODate = string; // 'YYYY-MM-DD'
export type ISOTimestamp = string; // '2026-09-24T10:00:00.000Z'

// ── Workflow ────────────────────────────────────────────────────────────────

export const APPLICATION_STATES = [
  'Submitted',
  'Validated',
  'CorrectionRequested',
  'ReadyForEvaluation',
  'Evaluated',
  'EvaluationFailed',
  'UnderReview',
  'Approved',
  'CardIssued',
  'Rejected',
  'Revoked',
  'Expired',
] as const;
export type ApplicationState = (typeof APPLICATION_STATES)[number];

export const ROLES = ['pilot', 'evaluator', 'assistant', 'sdm', 'dc', 'responder', 'admin', 'verifier'] as const;
export type Role = (typeof ROLES)[number];

export const CASE_ACTIONS = [
  'assign',
  'note',
  'requestCorrection',
  'evaluate',
  'recommend',
  'approve',
  'reject',
  'revoke',
  'reissue',
  'markPrinted',
  'override',
  'reopen',
] as const;
export type CaseActionName = (typeof CASE_ACTIONS)[number];

export type RiskTier = 'green' | 'amber' | 'red';
export type Severity = 'pass' | 'warn' | 'fail';

// ── Validation ──────────────────────────────────────────────────────────────

export interface Finding {
  rule: string; // e.g. 'mrz.checkdigits'
  severity: Severity;
  expected?: string;
  found?: string;
  evidence?: string;
  explanation: string; // plain language, shown to pilot and officer
  overridden?: { by: string; reason: string; at: ISOTimestamp };
}

export interface MrzResult {
  ok: boolean;
  reason?: string;
  checks: { docNo: boolean; dob: boolean; expiry: boolean; composite: boolean };
  documentNumber: string;
  issuingCountry: string;
  nationality: string;
  surname: string;
  givenNames: string;
  sex: string;
  dateOfBirth: ISODate;
  dateOfExpiry: ISODate;
  personalNumber: string;
  line1: string;
  line2: string;
}

// ── Application fields (what the pilot fills) ───────────────────────────────

export type DocType = 'passport' | 'aadhaar' | 'voter_id' | 'driving_licence' | 'other';
export type GliderClass = 'EN-A' | 'EN-B' | 'EN-C' | 'EN-D' | 'CCC' | 'DHV-1' | 'DHV-1-2' | 'DHV-2' | 'DHV-2-3' | 'DHV-3' | 'unknown';

export interface PilotFields {
  givenNames: string;
  surname: string;
  fathersName: string;
  addressLine: string;
  city: string;
  region: string;
  country: string; // ISO 3166-1 alpha-3
  postcode: string;
  nationality: string; // ISO 3166-1 alpha-3
  dob: ISODate;
  phone: string; // E.164
  email: string;

  docType: DocType;
  docNumber: string;
  docIssuingCountry: string;
  docExpiry: ISODate | '';
  mrzLine1: string;
  mrzLine2: string;

  isForeign: boolean;
  arrivalDate: ISODate | '';
  visaType: string;
  visaValidFrom: ISODate | '';
  visaValidTo: ISODate | '';

  certBody: string;
  certLevel: string;
  certNumber: string;
  certIssued: ISODate | '';
  certExpiry: ISODate | '';
  certHolderName: string;

  gliderMake: string;
  gliderModel: string;
  gliderSize: string;
  gliderClass: GliderClass;
  reserveMake: string;
  reserveModel: string;
  reserveRepack: ISODate | '';

  insProvider: string;
  insPolicyNo: string;
  insInsuredName: string;
  insCoverText: string; // free text or OCR of the cover wording; searched for cover phrases
  insValidTo: ISODate | '';
  medicalValidTo: ISODate | '';

  emgName: string;
  emgRelationship: string;
  emgPhone: string;
  bloodGroup: string;

  declarationAccepted: boolean[]; // 4 clauses
  bondAccepted: boolean[]; // 7 clauses
  typedName: string;
  consentPurpose: boolean;
}

export type FileSlot = 'doc' | 'photo' | 'cert' | 'ins' | 'signed' | 'card';

export interface UploadFile {
  slot: FileSlot;
  name: string;
  mime: string;
  data: string; // base64 (no data: prefix)
  sha256?: string;
}

// ── Stored application (one row of the register) ────────────────────────────

export interface Application {
  ref: string; // A-<season>-<8 hex>
  status: ApplicationState;
  season: string;
  createdAt: ISOTimestamp;
  updatedAt: ISOTimestamp;
  idemKey: string;
  statusTokenHash: string;

  fields: PilotFields;
  mrz: MrzResult | null;

  findings: Finding[];
  riskTier: RiskTier;
  rulesetVersion: string;

  assignedTo?: string;
  evaluatorUser?: string;
  assessmentOutcome?: 'pass' | 'fail' | 'conditional';
  assessmentNotes?: string;
  assessmentChecklist?: Record<string, boolean>;
  assessedAt?: ISOTimestamp;

  reviewerUser?: string;
  recommendation?: 'approve' | 'reject';
  reviewedAt?: ISOTimestamp;

  approverUser?: string;
  decision?: 'approved' | 'rejected';
  decisionReason?: string;
  decidedAt?: ISOTimestamp;

  correctionRequest?: { items: string[]; message: string; requestedBy: string; at: ISOTimestamp } | null;
  notes?: { by: string; at: ISOTimestamp; text: string }[];

  card?: CardRecord | null;
  files: Partial<Record<FileSlot, string>>; // slot → file id
  driveFolderId?: string;
  photoSha256?: string;
}

export interface CardRecord {
  cardNo: string; // BB-2026-000123
  validFrom: ISODate;
  validTo: ISODate;
  keyId: string;
  payload: string; // the exact signed string (without signature)
  signature: string; // base64url
  photoHash12: string;
  issuedAt: ISOTimestamp;
  printedAt?: ISOTimestamp;
  printSheet?: string;
  revokedAt?: ISOTimestamp;
  revocationReason?: string;
  reissuedFrom?: string;
}

export interface AuditRow {
  seq: number;
  at: ISOTimestamp;
  actor: string;
  role: Role | 'system';
  action: string;
  entity: 'application' | 'card' | 'staff' | 'config' | 'system';
  entityId: string;
  fromState: string;
  toState: string;
  reason: string;
}

export interface Staff {
  user: string;
  displayName: string;
  role: Role;
  active: boolean;
  notes?: string;
}

export interface ScanRow {
  at: ISOTimestamp;
  cardNo: string;
  deviceId: string;
  result: 'green' | 'amber' | 'red';
  reason: string;
  offline: boolean;
  listAgeSeconds: number;
  lat?: number;
  lng?: number;
}

export interface RevocationList {
  version: number;
  generatedAt: ISOTimestamp;
  keyId: string;
  revoked: { cardNo: string; at: ISOTimestamp; reason?: string }[];
  signature: string; // Ed25519 over the canonical JSON of {version, generatedAt, keyId, revoked}
}

// ── Sessions ────────────────────────────────────────────────────────────────

export interface Session {
  user: string;
  role: Role;
  displayName: string;
  exp: number; // ms epoch
}

// ── Tenant configuration ────────────────────────────────────────────────────

export interface TenantKey {
  kid: string;
  publicKey: string; // base64url, 32 bytes
  validFrom: ISODate;
  validTo: ISODate;
  note?: string;
}

export interface TenantConfig {
  tenantId: string;
  office: {
    name: string;
    shortName: string;
    district: string;
    state: string;
    site: string;
    siteCode: string;
    issuingAuthority: string;
    address: string;
    phone: string;
    email: string;
    whatsapp: string;
    grievanceOfficer: string;
    emblem: string;
  };
  product: {
    name: string;
    cardName: string;
    tagline: string;
    publicUrl: string;
    languages: string[];
    defaultLanguage: string;
  };
  season: {
    key: string;
    label: string;
    validFrom: ISODate;
    validTo: ISODate;
    cardValidityDays: number; // 0 = season end
    cardPrefix: string;
  };
  theme: { primary: string; primaryStrong: string; accent: string; ink: string };
  rules: {
    rulesetVersion: string;
    nameMatchPass: number;
    nameMatchWarn: number;
    recognisedCertBodies: string[];
    certLevels: string[];
    gliderClassMaxByLevel: Record<string, string>;
    insuranceCoverPhrases: string[];
  };
  keys: TenantKey[];
  retention: { applicationYears: number; documentYearsAfterExpiry: number; scanLogYears: number };
  verify: { revocationListPath: string; staleAfterHours: number };
}

// ── Dashboard aggregates ────────────────────────────────────────────────────

export interface StatsSnapshot {
  generatedAt: ISOTimestamp;
  season: string;
  totals: {
    applications: number;
    byState: Record<ApplicationState, number>;
    cardsIssued: number;
    cardsActive: number;
    cardsRevoked: number;
    indian: number;
    foreign: number;
  };
  byNationality: { nationality: string; count: number }[];
  byDay: { date: ISODate; submissions: number; issued: number }[];
  byTier: Record<RiskTier, number>;
  byEvaluator: { user: string; count: number; pass: number; fail: number }[];
  queueAges: { state: ApplicationState; count: number; oldestHours: number }[];
  expiringIn7Days: number;
  avgApprovalHours: number | null;
  topFindings: { rule: string; count: number }[];
}
